import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { NewTicket, Ticket, TicketPriority, TicketStatus, TicketUser, CustomerSummary } from 'src/api/models';
import { CustomerService, TicketService } from 'src/api/services';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { Options } from 'sortablejs';
import { SupportTicketEditorModalComponent } from 'src/app/components/support-ticket-editor-modal/support-ticket-editor-modal.component';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import TurndownService from 'turndown';

@Component({
  selector: 'app-support-home',
  templateUrl: './support-home.component.html',
  styleUrls: ['./support-home.component.css']
})
export class SupportHomeComponent implements OnInit {
  @ViewChild('ticketEditor', { static: false }) ticketEditor: SupportTicketEditorModalComponent;

  priorityTypes : TicketPriority[] = [];
  statusTypes : TicketStatus[] = [];
  tickets : Ticket[] = [];
  notStarted : Ticket[] = [];
  inProgress : Ticket[] = [];
  completed : Ticket[] = [];
  users : TicketUser[] = [];
  allCustomers: CustomerSummary[] = [];

  newTicket: NewTicket = {priorityId:3} as NewTicket;
  newTicketDescriptionHtml = '';
  newTicketCustomerId: number;
  newNote: string = '';
  selectedTicket: Ticket | null = null;
  showClosedTickets: boolean = false;
  closedTickets: Ticket[] = [];

  columnsOrder: string[] = ['Not Started', 'In Progress', 'Completed'];
  columns : { [name:string]: Ticket[]} = {
    'Not Started': [],
    'In Progress': [],
    'Completed': []
  };
  sortNewestFirstByColumn: { [name: string]: boolean } = {
    'Not Started': false,
    'In Progress': false,
    'Completed': false
  };
  sortHighestSlaFirstByColumn: { [name: string]: boolean } = {
    'Not Started': true,
    'In Progress': true,
    'Completed': true
  };
  columnSortModeByColumn: { [name: string]: 'created' | 'sla' } = {
    'Not Started': 'created',
    'In Progress': 'created',
    'Completed': 'created'
  };
  ticketQuery : string = 'My Tickets';
  ticketsLoading = false;
  expandedTickets = new Set<number>();

  newTicketEditorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '100%',
    minHeight: '250px',
    translate: 'no',
    sanitize: true,
    placeholder: 'Enter ticket description',
    toolbarHiddenButtons: [
      [
        'undo',
        'redo',
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyLeft',
        'justifyCenter',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertOrderedList',
        'fontName'
      ],
      [
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
        'insertHorizontalRule',
        'removeFormat',
        'toggleEditorMode'
      ]
    ]
  };

  private newTicketTurndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced'
  });

  private getTicketCreatedDateMs(ticket: Ticket): number {
    const timestamp = Date.parse(ticket.createdDate as unknown as string);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private sortTicketsByCreatedDate(tickets: Ticket[], newestFirst: boolean): Ticket[] {
    return [...tickets].sort((a, b) => {
      const aTime = this.getTicketCreatedDateMs(a);
      const bTime = this.getTicketCreatedDateMs(b);
      return newestFirst ? bTime - aTime : aTime - bTime;
    });
  }

  private sortTicketsBySlaPercent(tickets: Ticket[], highestFirst: boolean): Ticket[] {
    return [...tickets].sort((a, b) => {
      const aPercent = this.getDisplaySlaPercent(a);
      const bPercent = this.getDisplaySlaPercent(b);

      // Always place null SLA values at the end.
      if (aPercent == null && bPercent == null) {
        return this.getTicketCreatedDateMs(a) - this.getTicketCreatedDateMs(b);
      }
      if (aPercent == null) {
        return 1;
      }
      if (bPercent == null) {
        return -1;
      }

      return highestFirst ? bPercent - aPercent : aPercent - bPercent;
    });
  }

  private applyColumnSorting(): void {
    for (const column of this.columnsOrder) {
      const source = this.columns[column] || [];
      if (this.columnSortModeByColumn[column] === 'sla') {
        this.columns[column] = this.sortTicketsBySlaPercent(source, !!this.sortHighestSlaFirstByColumn[column]);
      } else {
        this.columns[column] = this.sortTicketsByCreatedDate(source, !!this.sortNewestFirstByColumn[column]);
      }
    }
  }

  private rebuildColumns(): void {
    this.columns['Not Started'] = this.tickets.filter(ticket => ticket.status.id < 3);
    this.columns['In Progress'] = this.tickets.filter(ticket => ticket.status.id == 3);
    const completed = this.tickets.filter(ticket => ticket.status.id == 4);
    this.columns['Completed'] = this.showClosedTickets ? [...completed, ...this.closedTickets] : completed;
    this.applyColumnSorting();
  }

  toggleColumnSort(column: string): void {
    this.columnSortModeByColumn[column] = 'created';
    this.sortNewestFirstByColumn[column] = !this.sortNewestFirstByColumn[column];
    this.applyColumnSorting();
  }

  isColumnSortNewestFirst(column: string): boolean {
    return !!this.sortNewestFirstByColumn[column];
  }

  toggleColumnSlaSort(column: string): void {
    this.columnSortModeByColumn[column] = 'sla';
    this.sortHighestSlaFirstByColumn[column] = !this.sortHighestSlaFirstByColumn[column];
    this.applyColumnSorting();
  }

  isColumnSortBySla(column: string): boolean {
    return this.columnSortModeByColumn[column] === 'sla';
  }

  isColumnSlaSortHighestFirst(column: string): boolean {
    return !!this.sortHighestSlaFirstByColumn[column];
  }

  toggleTicketDetail(id: number): void {
    if (this.expandedTickets.has(id)) {
      this.expandedTickets.delete(id);
    } else {
      this.expandedTickets.add(id);
    }
  }

  getDisplaySlaPercent(ticket: Ticket): number | null {
    const source = ticket as Ticket & {
      DisplaySlaPercent?: number;
      displaySlaPercent?: number;
    };

    const raw = source.displaySlaPercent ?? source.DisplaySlaPercent ?? source.acknowledgeSlaRemainingPercent;
    if (raw == null || Number.isNaN(raw)) {
      return null;
    }

    return Math.min(100, Math.max(0, raw));
  }

  shouldShowSlaClock(ticket: Ticket): boolean {
    return this.getDisplaySlaPercent(ticket) != null;
  }

  getSlaProgressFillPercent(ticket: Ticket): number {
    const remainingPercent = this.getDisplaySlaPercent(ticket);
    if (remainingPercent == null) {
      return 0;
    }

    return 100 - remainingPercent;
  }

  getSlaProgressClass(ticket: Ticket): string {
    const percent = this.getDisplaySlaPercent(ticket);
    if (percent == null) {
      return 'ticket-sla-progress__fill--default';
    }

    if (percent < 25) {
      return 'ticket-sla-progress__fill--danger';
    }

    if (percent < 50) {
      return 'ticket-sla-progress__fill--warning';
    }

    return 'ticket-sla-progress__fill--default';
  }

  getSlaMetricCardClass(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return 'ticket-sla-dashboard__item--na';
    }

    if (value < 25) {
      return 'ticket-sla-dashboard__item--danger';
    }

    if (value < 50) {
      return 'ticket-sla-dashboard__item--warning';
    }

    return 'ticket-sla-dashboard__item--default';
  }

  getSlaLevelCardClass(level?: string | null): string {
    const normalized = (level || '').toLowerCase();
    if (normalized.includes('gold')) {
      return 'ticket-sla-dashboard__item--gold';
    }
    if (normalized.includes('silver')) {
      return 'ticket-sla-dashboard__item--silver';
    }
    if (normalized.includes('bronze')) {
      return 'ticket-sla-dashboard__item--bronze';
    }
    return 'ticket-sla-dashboard__item--na';
  }

  private getSlaTotalHours(ticket: Ticket, metric: 'acknowledge' | 'complete'): number | null {
    if (metric === 'acknowledge') {
      return ticket.acknowledgeSlaTotalHours ?? null;
    }

    return ticket.completeSlaTotalHours ?? ticket.resolveSlaThresholdHours ?? null;
  }

  private getSlaRemainingHours(ticket: Ticket, metric: 'acknowledge' | 'complete'): number | null {
    const source = ticket as Ticket & {
      acknowledgeSlaRemainingHours?: number;
      completeSlaRemainingHours?: number;
      AcknowledgeSlaRemainingHours?: number;
      CompleteSlaRemainingHours?: number;
    };

    const explicitRemaining = metric === 'acknowledge'
      ? (source.acknowledgeSlaRemainingHours ?? source.AcknowledgeSlaRemainingHours)
      : (source.completeSlaRemainingHours ?? source.CompleteSlaRemainingHours);

    if (explicitRemaining != null && !Number.isNaN(explicitRemaining)) {
      return explicitRemaining;
    }

    const percent = metric === 'acknowledge'
      ? ticket.acknowledgeSlaRemainingPercent
      : ticket.completeSlaRemainingPercent;
    const total = this.getSlaTotalHours(ticket, metric);
    if (percent == null || total == null || Number.isNaN(percent) || Number.isNaN(total)) {
      return null;
    }

    return (Math.max(0, Math.min(100, percent)) / 100) * total;
  }

  getSlaHoursDisplay(ticket: Ticket, metric: 'acknowledge' | 'complete'): string {
    const remaining = this.getSlaRemainingHours(ticket, metric);
    const total = this.getSlaTotalHours(ticket, metric);

    if (remaining == null || total == null) {
      return 'N/A';
    }

    return `${remaining.toFixed(1)}h / ${total.toFixed(1)}h`;
  }

  options: Options = {
    group: 'test',
    onAdd: (event) => {
      const columnName = event.target['data-column'];
      if (columnName) {
        const ticketId = event.item['data-id'];
        const ticket = this.tickets.find(ticket => ticket.id === parseInt(ticketId));
        if (ticket) {
          // Update the ticket's status based on the column
          if (columnName === 'Not Started') {
            ticket.status.id = 2; // Assuming 1 is 'Not Started'
          } else if (columnName === 'In Progress') {
            ticket.status.id = 3; // Assuming 3 is 'In Progress'
             if (ticket.assignedToUserId == null) {
              ticket.assignedToUserId = this.azureAuthService.userProfile?.userId;
            }
          } else if (columnName === 'Completed') {
            ticket.status.id = 4; // Assuming 5 is 'Completed'
          }
          this.selectedTicket = ticket;
          this.updateTicketStatus();
        }
      }
    },
    onUpdate: (event) => {
      const columnName = event.target['data-column'];
      console.log('Column name:', columnName);
      if (columnName) {
        const ticketId = event.item['data-id'];
        const ticket = this.tickets.find(ticket => ticket.id === parseInt(ticketId));
        //this.columns[columnName]
        console.log('Updated column:', columnName, 'Tickets:', ticket);
        if (ticket) {
          // Update the ticket's status based on the column
          if (columnName === 'Not Started') {
            ticket.status.id = 2; // Assuming 1 is 'Not Started'
          } else if (columnName === 'In Progress') {
            ticket.status.id = 3; // Assuming 3 is 'In Progress'
          } else if (columnName === 'Completed') {
            ticket.status.id = 4; // Assuming 5 is 'Completed'
          }
          this.selectedTicket = ticket;
          this.updateTicketStatus();
        }
        //this.changeDetectorRef.detectChanges();
      }
    }
  };

  constructor(
    private route: ActivatedRoute,
    private app: ApplicationConfigService,
    public azureAuthService: AzureAuthService,
    public ticketService: TicketService,
    private customerService: CustomerService,
    private toasterService: ToastrService) {
      app.customerChanged$.subscribe(cid => {
        this.ngOnInit();
      });
  }

  ngOnInit() {
    if (this.app.customerId == null){
      this.app.customerId = this.azureAuthService.userProfile.customerId;
    }
    this.ticketService.TicketGetTicketStatuses().subscribe(r => {
      this.statusTypes = r;
      console.log('Ticket statuses loaded:', this.statusTypes);
    });
    this.ticketService.TicketGetTicketPriorities().subscribe(r => {
      this.priorityTypes = r;
      console.log('Ticket priorities loaded:', this.priorityTypes);
    });
    this.ticketService.TicketGetAssignableUsers(this.app.customerId).subscribe(r => {
      this.users = r;
    });
    this.customerService.CustomerGetAllCustomers(false).subscribe(r => {
      this.allCustomers = (r || []).sort((a, b) => (a.customerName ?? '').localeCompare(b.customerName ?? ''));
    });
    this.changeQuery(this.ticketQuery);
  }

  changeQuery(query: string) {
    this.ticketQuery = query;
    this.ticketsLoading = true;
    this.columns = {
      'Not Started': [],
      'In Progress': [],
      'Completed': []
    };
    if (this.ticketQuery === 'My Tickets') {
      this.ticketService.TicketGetMyTickets({customerId:this.app.customerId, includeUnassigned: true}).subscribe(t => {
        this.tickets = t;
        this.rebuildColumns();
        if (this.showClosedTickets) { this.loadClosedTickets(); }
        this.ticketsLoading = false
      }, () => this.ticketsLoading = false);
    } else if (this.ticketQuery === 'Customer Tickets') {
      this.ticketService.TicketGetCustomerTickets(this.app.customerId).subscribe(t => {
        this.tickets = t;
        this.rebuildColumns();
        if (this.showClosedTickets) { this.loadClosedTickets(); }
        this.ticketsLoading = false
      }, () => this.ticketsLoading = false);
    }
  }

  toggleClosedTickets() {
    this.showClosedTickets = !this.showClosedTickets;
    if (this.showClosedTickets) {
      this.loadClosedTickets();
    } else {
      this.closedTickets = [];
      this.rebuildColumns();
    }
  }

  loadClosedTickets() {
    this.ticketService.TicketGetMyClosedTickets({customerId: this.app.customerId, includeUnassigned: true}).subscribe(t => {
      this.closedTickets = t;
      this.rebuildColumns();
    }, error => {
      this.toasterService.error('Failed to load closed tickets: ' + error.message);
    });
  }

  startCreateTicket() {
    this.newTicket = {priorityId:3} as NewTicket;
    this.newTicketDescriptionHtml = '';
    this.newTicketCustomerId = this.app.customerId;
  }

  onNewTicketDescriptionEditorChange(html: string): void {
    this.newTicketDescriptionHtml = html || '';
    this.newTicket.description = this.newTicketTurndownService.turndown(this.newTicketDescriptionHtml);
  }

  createTicket(){
    const customerId = this.azureAuthService.isAdmin() ? (this.newTicketCustomerId ?? this.app.customerId) : this.app.customerId;
    this.ticketService.TicketCreateTicket({customerId, ticket: this.newTicket}).subscribe(ticket => {
      this.toasterService.success('Ticket created successfully');
      this.tickets.push(ticket);
      this.rebuildColumns();
      this.newTicket = {priorityId:3} as NewTicket;
    }, error => {
      this.toasterService.error('Failed to create ticket: ' + error.message);
    });
  }

  addNote(){
    this.ticketService.TicketCreateTicketNote({
      ticketId: this.selectedTicket!.id,note: this.newNote
    }).subscribe(note => {
      this.toasterService.success('Note added successfully');
      if (this.selectedTicket) {
        this.selectedTicket.notes.push(note);
        this.newNote = '';
      }
    }, error => {
      this.toasterService.error('Failed to add note: ' + error.message);
    });
  }

  updateTicket(ticket?: Ticket) {
    const target = ticket ?? this.selectedTicket;
    if (!target) {
      return;
    }

    this.ticketService.TicketUpdateTicket({customerId: this.app.customerId, updateTicket: target}).subscribe(ticket => {
      this.toasterService.success('Ticket updated successfully');
      const index = this.tickets.findIndex(t => t.id === ticket.id);
      if (index !== -1) {
        this.tickets[index] = ticket;
        this.rebuildColumns();
      }
      if (this.selectedTicket && this.selectedTicket.id === ticket.id) {
        this.selectedTicket = ticket;
      }
    }, error => {
      this.toasterService.error('Failed to update ticket: ' + error.message);
    });
  }

  openEditTicket(ticket: Ticket): void {
    this.selectedTicket = ticket;
    this.ticketEditor?.open();
  }

  updateTicketStatus() {
    this.ticketService.TicketUpdateTicketStatus({ticketId: this.selectedTicket.id, statusId: this.selectedTicket.status.id}).subscribe(ticket => {
      this.toasterService.success('Ticket status updated successfully');
      const index = this.tickets.findIndex(t => t.id === ticket.id);
      if (index !== -1) {
        this.tickets[index] = ticket;
        this.rebuildColumns();
      }
    }, error => {
      this.toasterService.error('Failed to update ticket: ' + error.message);
    });
  }

  closeTicket(ticket: Ticket) {
     this.ticketService.TicketUpdateTicketStatus({ticketId: ticket.id, statusId: 5}).subscribe(ticket => {
      this.toasterService.success('Ticket status updated successfully');
      const index = this.tickets.findIndex(t => t.id === ticket.id);
      ticket.status.id = 5;
      if (index !== -1) {
        this.tickets[index] = ticket;
        this.rebuildColumns();
      }
    }, error => {
      this.toasterService.error('Failed to update ticket: ' + error.message);
    });
  }

  isTicketAcknowledged(ticket: Ticket): boolean {
    return !!ticket?.acknowledgedDate;
  }

  isTicketCompleted(ticket: Ticket): boolean {
    if (!ticket) {
      return false;
    }

    if (ticket.status?.id != null) {
      return ticket.status.id >= 4;
    }

    return !!ticket.resolvedDate || !!ticket.closedDate;
  }

  canAcknowledgeTicket(ticket: Ticket): boolean {
    if (!ticket || this.isTicketAcknowledged(ticket)) {
      return false;
    }

    if (this.azureAuthService.isAdmin()) {
      return true;
    }

    const currentUserId = this.azureAuthService.userProfile?.userId;
    if (currentUserId != null && ticket.assignedToUserId != null && currentUserId === ticket.assignedToUserId) {
      return true;
    }

    const currentName = this.azureAuthService.userProfile?.fullName;
    return !!currentName && !!ticket.assignedToUser && currentName === ticket.assignedToUser;
  }

  acknowledgeTicket(ticket: Ticket): void {
    if (!this.canAcknowledgeTicket(ticket)) {
      return;
    }

    this.ticketService.TicketAcknowledgeTicket(ticket.id).subscribe(updatedTicket => {
      this.toasterService.success('Ticket acknowledged successfully');
      const index = this.tickets.findIndex(t => t.id === updatedTicket.id);
      if (index !== -1) {
        this.tickets[index] = updatedTicket;
        this.rebuildColumns();
      }
      if (this.selectedTicket && this.selectedTicket.id === updatedTicket.id) {
        this.selectedTicket = updatedTicket;
      }
    }, error => {
      this.toasterService.error('Failed to acknowledge ticket: ' + error.message);
    });
  }

  acceptOwnership(ticket: Ticket) {
    ticket.assignedToUserId = this.azureAuthService.userProfile?.userId;
    this.ticketService.TicketUpdateTicket({customerId: this.app.customerId, updateTicket: ticket}).subscribe(updatedTicket => {
      this.toasterService.success('Ownership accepted successfully');
      const index = this.tickets.findIndex(t => t.id === updatedTicket.id);
      if (index !== -1) {
        this.tickets[index] = updatedTicket;
        this.rebuildColumns();
      }
    }, error => {
      this.toasterService.error('Failed to accept ownership: ' + error.message);
    });
  }

  getFilteredUsers(): TicketUser[] {
    if (!this.users || !this.selectedTicket) {
      return this.users || [];
    }

    return this.users.filter(user =>
      user.customerName === this.selectedTicket.customerName ||
      (user.email && user.email.includes('@indicium-dynamics.com.au'))
    );
  }

  get customerNames(): string[] {
    return this.allCustomers.map(c => c.customerName).filter(n => !!n) as string[];
  }

}
